import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useState } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { CloudConnectSubscription } from 'src/api/endpoints/cloudConnect.types';
import { FlexDivider, NotificationBox, TextWithLabel } from 'src/components';
import { useVerifyCloudConnectPaymentMethod } from 'src/utils/useVerifyCloudConnectPaymentMethod';

import { CardHeading } from './CardHeading';
import { CloudConnectChangePaymentMethodButton } from './CloudConnectChangePaymentMethodButton';
import { MissingPaymentDetailsNotification } from './MissingPaymentDetailsNotification';

export const CloudConnectPaymentMethodDetails = ({
  subscription,
}: {
  subscription: CloudConnectSubscription;
}) => {
  const { t } = useLingui();
  const paymentMethod = subscription.paymentMethod;
  const paymentMethodLabel = t`Payment method`;
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { redirect_status } = useSearch({ strict: false });
  const verifyPaymentMethod = useVerifyCloudConnectPaymentMethod();

  useEffect(() => {
    if (redirect_status === 'failed' || redirect_status === 'failure') {
      setError(true);
      void navigate({ to: '', search: '', replace: true });
    }
  }, [redirect_status]);

  return (
    <div className="flex flex-col gap-16">
      <CardHeading>{paymentMethodLabel}</CardHeading>

      <div className="flex flex-col gap-16 lm:flex-row lm:items-start">
        {paymentMethod?.type === 'card' && paymentMethod.card && (
          <div className="flex w-full flex-wrap gap-16 ls:flex-row">
            <TextWithLabel label={t`Payment card`} className="flex-1">
              {paymentMethod.card.brand.toUpperCase()} ••••{' '}
              {paymentMethod.card.last4}
            </TextWithLabel>

            <FlexDivider />

            <TextWithLabel label={t`Expires`} className="flex-1">
              {paymentMethod.card.expireMonth.toString().padStart(2, '0')} /{' '}
              {paymentMethod.card.expireYear.toString().slice(-2)}
            </TextWithLabel>
          </div>
        )}

        {paymentMethod?.type === 'paypal' && paymentMethod.paypal && (
          <TextWithLabel label={t`PayPal`} className="flex-1">
            {paymentMethod.paypal.email}
          </TextWithLabel>
        )}

        {paymentMethod?.type === 'invoice' && (
          <TextWithLabel label={paymentMethodLabel} className="flex-1">
            <Trans>Invoice</Trans>
          </TextWithLabel>
        )}

        <div className="flex flex-col items-end gap-16 ls:flex-row ls:flex-wrap lm:flex-col">
          <CloudConnectChangePaymentMethodButton
            componentLocation={ComponentLocations.MANAGE_CONNECT.PAYMENT_PAGE}
            isVerifying={verifyPaymentMethod.isPending}
          />
        </div>
      </div>

      {!paymentMethod && <MissingPaymentDetailsNotification />}

      {error && (
        <NotificationBox
          onClose={() => setError(false)}
          variant="error"
          title={t`An error occured while trying to authorize payment`}
          className="w-full"
          alwaysShow
        />
      )}
    </div>
  );
};
